import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Alert, Box, Button, Loader, Menu, TeamAvatar, Typography, UserAvatar, pxToRem } from '@pocketlaw/tetris';
import { ApprovalType } from 'shared/domains/apollo/generated/types';
import { useFuse } from 'shared/domains/common-utils/useFuse';
import { SelectVoluntaryApproversDocument } from './SelectVoluntaryApprovers.gql';
import { normalizeItems } from './normalizeItems';
import { useVoluntaryApprovers } from '../../../VoluntaryApproversProvider';
import { SendForVoluntaryApprovalModal } from '../../SendForVoluntaryApprovalModal';
export function SelectVoluntaryApprovers() {
    const { selectedUsers, selectedApprovers, selectedTeamApprovers, addApprover, addTeamApprover, removeApprover, removeTeamApprover, } = useVoluntaryApprovers();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [dialog, setDialog] = useState('hide');
    const { data, loading, error } = useQuery(SelectVoluntaryApproversDocument);
    const { teams, users } = normalizeItems(data);
    const { result, search, active } = useFuse([...teams, ...users], {
        keys: ['name', 'email'],
        minMatchCharLength: 2,
    });
    const activeSearch = active && query.length >= 2;
    const noResults = activeSearch && result.length === 0;
    const hasResults = activeSearch && result.length > 0;
    const selectedIds = [
        ...selectedApprovers.map((approver) => approver.userId),
        ...selectedTeamApprovers.map((teamApprover) => teamApprover.teamId),
    ];
    const disableSubmit = selectedIds.length === 0;
    const closeMenu = () => setOpen(false);
    const toggleMenu = () => setOpen(!open);
    const showDialog = (type) => {
        setDialog(type);
        closeMenu();
    };
    const hideDialog = () => setDialog('hide');
    const handleSearch = (value) => {
        setQuery(value);
        search(value);
    };
    const handleToggleTeam = (team) => {
        if (selectedTeamApprovers.some((teamApprover) => teamApprover.teamId === team.id)) {
            removeTeamApprover(team.id);
        }
        else {
            addTeamApprover({
                teamId: team.id,
                name: team.name,
                members: team.members || [],
            });
        }
    };
    const handleToggleUser = (user) => {
        if (selectedApprovers.some((approver) => approver.userId === user.id)) {
            removeApprover(user.id);
        }
        else {
            addApprover({
                userId: user.id,
                name: user.name,
                email: user.email || '',
            });
        }
    };
    const getMembersAmount = (members = 0) => plural(members, {
        0: 'No members',
        one: '# member',
        other: '# members',
    });
    if (error) {
        return (<Alert appearance="danger" message={t({
                message: 'Something went wrong, please reload the page and try again',
                comment: 'Document approval check - select approvers field - failed to load data to display',
            })}/>);
    }
    return (<Fragment>
      {dialog !== 'hide' && (<SendForVoluntaryApprovalModal approvalType={dialog === 'show:all' ? ApprovalType.All : ApprovalType.OneOf} selectedApprovers={selectedUsers} onClose={hideDialog}/>)}

      <Menu.Root side="top" align="end" open={open} onClose={closeMenu} minWidth={pxToRem(440)} maxWidth={pxToRem(440)} target={<Button $stretch size="small" disabled={loading} onClick={toggleMenu}>
            {loading && <Loader />}
            <Trans comment="Select approvers - Ask for approval button label">Ask for approval</Trans>
          </Button>}>
        <Menu.HeaderSearch value={query} onChange={handleSearch} placeholder={t({
            message: 'Search approvers...',
            comment: 'Select approvers - Search placeholder',
        })}/>

        {noResults && (<Box alignItems="center" justifyContent="center" py="xl">
            <Typography $appearance="100" $fontSize="small">
              <Trans comment="Select approvers - No results found">No results found</Trans>
            </Typography>
          </Box>)}

        {hasResults && (<Fragment>
            <Menu.Label>
              <Trans comment="Select approvers - Results">Results</Trans>
            </Menu.Label>

            {result.map((item) => {
                var _a;
                return (<Menu.ItemCheckbox key={item.id} checked={selectedIds.includes(item.id)} onChange={() => (item.type === 'team' ? handleToggleTeam(item) : handleToggleUser(item))}>
                {item.type === 'team' ? (<TeamAvatar size="sm" title={item.name} subtitle={getMembersAmount((_a = item.members) === null || _a === void 0 ? void 0 : _a.length)}/>) : (<UserAvatar size="sm" title={item.name} subtitle={item.email}/>)}
              </Menu.ItemCheckbox>);
            })}
          </Fragment>)}

        {!activeSearch && (<Fragment>
            <Menu.Label>
              <Trans comment="Select approvers - Teams">Teams</Trans>
            </Menu.Label>

            {teams.map((team) => {
                var _a;
                return (<Menu.ItemCheckbox key={team.id} checked={selectedIds.includes(team.id)} onChange={() => handleToggleTeam(team)}>
                <TeamAvatar size="sm" title={team.name} subtitle={getMembersAmount((_a = team.members) === null || _a === void 0 ? void 0 : _a.length)}/>
              </Menu.ItemCheckbox>);
            })}

            <Menu.Label>
              <Trans comment="Select approvers - Users">Users</Trans>
            </Menu.Label>

            {users.map((user) => (<Menu.ItemCheckbox key={user.id} checked={selectedIds.includes(user.id)} onChange={() => handleToggleUser(user)}>
                <UserAvatar size="sm" title={user.name} subtitle={user.email}/>
              </Menu.ItemCheckbox>))}
          </Fragment>)}

        <Menu.Footer sticky>
          <Box gap="md">
            <Button $stretch size="small" appearance="secondary" disabled={disableSubmit} onClick={() => showDialog('show:one-of')}>
              <Trans comment="Select approvers - Require by one of button label">Require by one of</Trans>
            </Button>
            <Button $stretch size="small" appearance="secondary" disabled={disableSubmit} onClick={() => showDialog('show:all')}>
              <Trans comment="Select approvers - Require by all button label">Require by all</Trans>
            </Button>
          </Box>
        </Menu.Footer>
      </Menu.Root>
    </Fragment>);
}
